import { z } from 'zod';

/**
 * The donations reconciliation of one tenant (INI-0034 S6/S7), as the API relays
 * it from VeevartBackend's runner: `ReconciliationDocument` and
 * `DonationsReconciliationReport` (`contractVersion: 2`) in
 * `domains/fundraising/migration/src/donations/reconciliation/`. The API already
 * refused another version; this parse keeps the screen honest about what it got.
 *
 * Unknown keys are stripped. `state` is closed (it decides whether to keep
 * polling); display tokens (`stage`, statuses, issue codes) stay open strings.
 */

export const RECONCILIATION_STATES = ['requested', 'running', 'completed', 'failed'] as const;
export const ReconciliationState = z.enum(RECONCILIATION_STATES);
export type ReconciliationState = z.infer<typeof ReconciliationState>;

const count = z.number().int().nonnegative();
const minor = z.number().int();

export const ReconciliationMoney = z.object({
    count,
    amountMinor: minor,
    receivedMinor: minor,
    refundedMinor: minor,
});
export type ReconciliationMoney = z.infer<typeof ReconciliationMoney>;

export const ReconciliationTotalsRow = z.object({
    fiscalYear: z.number().int().nullable(),
    stage: z.string(),
    salesforce: ReconciliationMoney,
    veevart: ReconciliationMoney.extend({ remainingMinor: minor }),
    veevartByStatus: z.record(z.string(), ReconciliationMoney),
    matches: z.boolean(),
});
export type ReconciliationTotalsRow = z.infer<typeof ReconciliationTotalsRow>;

export const ReconciliationIssue = z.object({
    code: z.string(),
    opportunityId: z.string().optional(),
    contributionId: z.string().optional(),
    commitmentId: z.string().optional(),
    paymentIds: z.array(z.string()).optional(),
    paymentCount: count.optional(),
    constituentId: z.string().optional(),
    fundId: z.string().optional(),
    field: z.string().optional(),
    fiscalYear: z.number().int().nullable().optional(),
    stage: z.string().optional(),
    salesforceValue: z.union([z.number(), z.string()]).nullable().optional(),
    veevartValue: z.union([z.number(), z.string()]).nullable().optional(),
});
export type ReconciliationIssue = z.infer<typeof ReconciliationIssue>;

export const ReconciliationCounts = z.object({
    salesforceDonations: count,
    trackingRows: count,
    migrated: count,
    v2Born: count,
    v2BornMissingInVeevart: count,
    notMigratedByDesign: count,
    notCopied: count,
    heldTwice: count,
    stampFailed: count,
    partialStamps: z.object({
        paymentLinkedDonationNot: count,
        donationLinkedPaymentsNot: count,
    }),
    flagged: z.object({
        wonRefundedInFull: count,
        receivedAndWrittenOffAboveAmount: count,
    }),
    donorsMissing: count,
    organizationsMissing: count,
    constituentsDiffer: count,
    amountsDiffer: count,
    fundSplitsDiffer: count,
    pendingBalance: z.object({
        withWarning: count,
        veevartRemainingAboveZero: count,
        balanceUnknown: count,
    }),
});
export type ReconciliationCounts = z.infer<typeof ReconciliationCounts>;

export const ReconciliationFiscalCalendar = z.object({
    startMonth: z.number().int().min(1).max(12),
    namedByStartYear: z.boolean(),
    customYears: count,
    customYearsReadable: z.boolean(),
});
export type ReconciliationFiscalCalendar = z.infer<typeof ReconciliationFiscalCalendar>;

export const ReconciliationPendingBalance = z.object({
    opportunityId: z.string(),
    contributionId: z.string(),
    commitmentId: z.string(),
    remainingMinor: minor,
    warned: z.boolean(),
});
export type ReconciliationPendingBalance = z.infer<typeof ReconciliationPendingBalance>;

export const DonationsReconciliationReport = z.object({
    contractVersion: z.literal(2),
    moduleId: z.literal('fundraising-donations'),
    tenantId: z.string(),
    requestId: z.string(),
    generatedAt: z.string(),
    outcome: z.enum(['reconciled', 'differences-found']),
    fiscalCalendar: ReconciliationFiscalCalendar,
    counts: ReconciliationCounts,
    totals: z.array(ReconciliationTotalsRow),
    issueCountsByCode: z.record(z.string(), count),
    issueCount: count,
    issues: z.array(ReconciliationIssue),
    issuesTruncated: z.boolean(),
    pendingBalances: z.array(ReconciliationPendingBalance),
    pendingBalancesTruncated: z.boolean(),
});
export type DonationsReconciliationReport = z.infer<typeof DonationsReconciliationReport>;

export const ReconciliationDocument = z.object({
    contractVersion: z.literal(2),
    requestId: z.string(),
    state: ReconciliationState,
    requestedAt: z.string(),
    requestedVia: z.enum(['operator', 'internal']),
    requestedByUserId: z.string().optional(),
    startedAt: z.string().optional(),
    finishedAt: z.string().optional(),
    failure: z.object({ code: z.string(), message: z.string() }).optional(),
    report: DonationsReconciliationReport.optional(),
});
export type ReconciliationDocument = z.infer<typeof ReconciliationDocument>;

export const ReconciliationResponse = z.object({ reconciliation: ReconciliationDocument });
